import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Share2, Tag, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, errMsg } from "../lib/api";
import { Countdown } from "../components/Marketplace";
import { DealGridCard } from "./Deals";
import { useAuth } from "../context/AuthContext";
import { PipeText } from "../lib/pipeText";

const inp = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-rose-500 text-sm";

function ClaimBox({ d }) {
  const { user } = useAuth();
  const [f, setF] = useState({ name: user?.name || "", email: user?.email || "" });
  const [claimed, setClaimed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const soldOut = d.status === "sold_out";
  const scheduled = d.status === "scheduled";
  const expired = d.status === "expired";
  const isPromo = !!d.promo_code;
  const claim = async () => {
    if (!f.name || !f.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try { const { data } = await api.post(`/deals/${d.id}/claim`, f); setClaimed(data.code); toast.success("Deal claimed!"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const copyPromo = async () => {
    try { await navigator.clipboard.writeText(d.promo_code); setCopied(true); toast.success("Promo code copied"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Copy failed — select the code manually"); }
  };
  if (isPromo) {
    return revealed ? (
      <div className="space-y-3" data-testid="deal-promo-revealed">
        <div className="flex items-center gap-2 bg-black/50 border border-rose-500/40 rounded-xl p-3">
          <code className="flex-1 font-mono font-black text-lg text-rose-300 tracking-widest text-center select-all" data-testid="deal-promo-code">{d.promo_code}</code>
          <button onClick={copyPromo} className="shrink-0 p-2.5 rounded-lg bg-rose-500 hover:bg-rose-600" title="Copy code" data-testid="deal-promo-copy">
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </button>
        </div>
        <Link to="/marketplace" className="block text-center bg-rose-500 hover:bg-rose-600 font-bold py-3 rounded-lg" data-testid="deal-promo-shop">Shop the Marketplace →</Link>
        {d.redemption_instructions && <p className="text-xs text-zinc-400">{d.redemption_instructions}</p>}
      </div>
    ) : (
      <button onClick={() => setRevealed(true)} disabled={soldOut || scheduled || expired} className="w-full bg-rose-500 hover:bg-rose-600 font-bold py-3 rounded-lg disabled:opacity-40 flex items-center justify-center gap-2" data-testid="reveal-promo-btn">
        <Tag size={15} /> {soldOut ? "Sold out" : scheduled ? "Coming soon" : expired ? "Expired" : "Reveal Promo"}
      </button>
    );
  }
  if (claimed) return (
    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4" data-testid="deal-claimed">
      <p className="text-emerald-300 font-bold">Claimed! Your code: {claimed}</p>
      {d.redemption_instructions && <p className="text-sm text-zinc-400 mt-1">{d.redemption_instructions}</p>}
    </div>
  );
  return (
    <div className="space-y-2" data-testid="deal-claim-box">
      <input className={inp} placeholder="Your name" value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} data-testid="claim-name" />
      <input type="email" className={inp} placeholder="Your email" value={f.email} onChange={(e) => setF((s) => ({ ...s, email: e.target.value }))} data-testid="claim-email" />
      <button onClick={claim} disabled={busy || soldOut || scheduled || expired} className="w-full bg-rose-500 hover:bg-rose-600 font-bold py-3 rounded-lg disabled:opacity-40 flex items-center justify-center gap-2" data-testid="claim-deal-btn">
        {busy && <Loader2 size={15} className="animate-spin" />}{soldOut ? "Sold out" : scheduled ? "Coming soon" : expired ? "Expired" : "Claim deal"}
      </button>
    </div>
  );
}

export default function DealDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(undefined);
  useEffect(() => { api.get(`/deals/${slug}`).then((r) => setD(r.data)).catch(() => setD(null)); window.scrollTo(0, 0); }, [slug]);
  useEffect(() => { if (d) document.title = d.seo_title || `${d.title} — Gulf Coast VIP Deals`; }, [d]);

  if (d === undefined) return <div className="py-40 flex justify-center text-zinc-500"><Loader2 className="animate-spin" /></div>;
  if (!d) return <div className="py-40 text-center text-zinc-400">Deal not found. <Link to="/deals" className="text-rose-400">Browse deals →</Link></div>;

  const share = () => {
    if (navigator.share) navigator.share({ title: d.title, url: window.location.href }).catch(() => {});
    else { navigator.clipboard.writeText(window.location.href); toast("Link copied"); }
  };
  const left = d.quantity_available ? d.quantity_available - (d.quantity_claimed || 0) : null;

  return (
    <div className="px-6 md:px-12 lg:px-20 py-10 max-w-6xl mx-auto" data-testid="deal-detail-page">
      <div className="grid lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-7">
          {d.image && <img src={fileUrl(d.image)} alt={d.title} className="w-full aspect-[16/9] object-cover rounded-2xl border border-white/10" />}
          <div className="mt-6">
            {d.category && <p className="overline mb-2 flex items-center gap-2"><Tag size={13} /> {d.category}</p>}
            <h1 className="font-display font-extrabold text-3xl md:text-4xl" data-testid="deal-title">{d.title}</h1>
            {d.venue && <Link to={`/venues/${d.venue.slug}`} className="mt-2 inline-flex items-center gap-1.5 text-rose-400 hover:underline" data-testid="deal-venue-link"><MapPin size={14} /> {d.venue.name}{d.city ? ` · ${d.city}, ${d.state}` : ""}</Link>}
            {d.short_description && <p className="text-zinc-300 mt-4">{d.short_description}</p>}
            {d.description && <PipeText text={d.description} className="text-zinc-400 mt-3" />}
            {d.redemption_instructions && (
              <div className="mt-6 bg-[#111] border border-white/10 rounded-xl p-4">
                <p className="overline mb-1">How to redeem</p>
                <p className="text-sm text-zinc-300">{d.redemption_instructions}</p>
              </div>
            )}
            {d.terms && (
              <div className="mt-4 bg-[#111] border border-white/10 rounded-xl p-4">
                <p className="overline mb-1">Terms & conditions</p>
                <p className="text-xs text-zinc-500">{d.terms}</p>
              </div>
            )}
            {d.venue?.address && <p className="text-sm text-zinc-500 mt-4 flex items-center gap-1"><MapPin size={13} /> {d.venue.address}, {d.venue.city}, {d.venue.state}</p>}
          </div>
        </div>
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <div className="bg-[#111] border border-rose-500/30 rounded-2xl p-6">
            <div className="flex items-end gap-3">
              {d.discounted_price && <span className="font-display font-black text-4xl text-rose-400">${d.discounted_price}</span>}
              {d.original_price && <span className="text-zinc-500 line-through mb-1.5">${d.original_price}</span>}
              {d.discount_percentage && <span className="ml-auto bg-rose-500 text-white font-black text-sm rounded-lg px-2.5 py-1">-{d.discount_percentage}%</span>}
            </div>
            {d.end_datetime && d.status === "active" && (
              <div className="mt-4 bg-black/40 rounded-xl p-3">
                <p className="text-[0.65rem] uppercase tracking-widest text-zinc-500 mb-2">Ends in</p>
                <Countdown to={d.end_datetime} endLabel="Expired" />
              </div>
            )}
            {d.status === "scheduled" && d.start_datetime && <p className="mt-3 text-sm text-amber-400">Starts {new Date(d.start_datetime).toLocaleString()}</p>}
            {left != null && <p className="text-xs text-zinc-500 mt-3" data-testid="deal-qty-left">{left <= 0 ? "Sold out" : `${left} of ${d.quantity_available} left`}</p>}
            <div className="mt-4"><ClaimBox d={d} /></div>
            <button onClick={share} className="mt-3 w-full border border-white/15 py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-white/5" data-testid="deal-share"><Share2 size={14} /> Share this deal</button>
          </div>
        </div>
      </div>
      {d.related_deals?.length > 0 && (
        <div className="mt-14">
          <p className="overline mb-5">Related deals</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">{d.related_deals.map((r) => <DealGridCard key={r.id} d={r} />)}</div>
        </div>
      )}
    </div>
  );
}
